import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, ArrowRight, Home, Globe, Check } from "lucide-react";
import { api, API, formatApiError } from "../../lib/api";
import { Reveal, Eyebrow } from "./motion";

const PACKS = {
  domestic: {
    label: "Caymanian / Resident Buyer",
    icon: Home,
    blurb: "Includes first-time buyer stamp-duty concessions and local financing terms.",
  },
  foreign: {
    label: "Overseas / Foreign Buyer",
    icon: Globe,
    blurb: "Covers no-restriction ownership, residency options and the Global Citizen Program.",
  },
};

const HIGHLIGHTS = [
  "Ownership, title & the guaranteed Land Registry",
  "Stamp duty, fees & Cayman's tax-free advantages",
  "Mortgages & financing terms",
  "The step-by-step buying process",
];

export default function BuyerGuide() {
  const [pack, setPack] = useState("domestic");
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const dl = (url) => (url.startsWith("/api/") ? `${API}${url.slice(4)}` : `${API}${url}`);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/resource-pack", { ...form, pack });
      window.open(dl(data.download_url), "_blank");
      toast.success("Your ownership pack is downloading in a new tab.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-[#FAF9F6]/30 py-3 text-[#FAF9F6] placeholder:text-[#FAF9F6]/50 focus:border-[var(--gold)] focus:outline-none transition-colors";

  return (
    <section id="guide" data-testid="buyer-guide-section" className="bg-[#0A1128] text-[#FAF9F6] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>Free Resource</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-5 text-balance">
              Your Cayman <span className="italic text-[var(--gold)]">Ownership Pack</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[#FAF9F6]/70 mt-6 max-w-md leading-relaxed">
              A concise, up-to-date guide to owning property in the Cayman Islands — tailored to whether you&apos;re buying as a Caymanian resident or from overseas.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-3 text-[#FAF9F6]/80 text-sm">
              {HIGHLIGHTS.map((t) => (
                <li key={t} className="flex items-center gap-3"><ArrowRight size={15} className="text-[var(--gold)]" /> {t}</li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-6">
          <div className="bg-[#FAF9F6]/[0.06] border border-[#FAF9F6]/15 p-8 md:p-10 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen size={22} className="text-[var(--gold)]" />
              <span className="font-serif text-2xl">Choose Your Pack</span>
            </div>

            {done ? (
              <div data-testid="guide-success" className="py-6">
                <p className="font-serif text-2xl text-[var(--gold)] mb-2">Enjoy the read.</p>
                <p className="text-[#FAF9F6]/70 text-sm">Your download opened in a new tab. Questions on any of it? I&apos;m one message away.</p>
                <a href={dl(`/api/resource-pack/${pack}/download`)} target="_blank" rel="noreferrer" data-testid="guide-redownload" className="inline-block mt-4 text-[var(--gold)] link-underline">Download again</a>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="guide-form" className="space-y-6">
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(PACKS).map(([key, p]) => {
                    const Icon = p.icon;
                    const active = pack === key;
                    return (
                      <button type="button" key={key} data-testid={`pack-${key}`} onClick={() => setPack(key)}
                        className={`text-left p-4 border transition-colors duration-300 ${active ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-[#FAF9F6]/20 hover:border-[#FAF9F6]/40"}`}>
                        <div className="flex items-center justify-between">
                          <Icon size={18} className={active ? "text-[var(--gold)]" : "text-[#FAF9F6]/70"} />
                          {active && <Check size={15} className="text-[var(--gold)]" />}
                        </div>
                        <div className="font-medium text-sm mt-3">{p.label}</div>
                        <div className="text-[11px] text-[#FAF9F6]/55 mt-1 leading-snug">{p.blurb}</div>
                      </button>
                    );
                  })}
                </div>
                <input data-testid="guide-name" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
                <input data-testid="guide-email" required type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
                <button data-testid="guide-submit" disabled={loading}
                  className="w-full py-4 bg-[var(--gold)] text-[#0A1128] text-sm uppercase tracking-[0.14em] font-medium hover:bg-[#d4b06a] transition-colors duration-300 disabled:opacity-60">
                  {loading ? "Preparing…" : "Get the Free Pack"}
                </button>
                <p className="text-xs text-[#FAF9F6]/40">No spam — just the pack and the occasional market update you can opt out of any time.</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
